/** Generation input/config fingerprints and drift state for Canvas nodes.
 *
 * UPSTREAM_STALE — semantic input identity changed (asset replacement, new output).
 * CONFIG_DIRTY — prompt / model / params changed on this node.
 * CLEAN — output still matches current upstream + config.
 */

import type { TFunction } from 'i18next'
import type { ImageInputMaterial, InputMaterial, KeyframeAssignment, KeyframeSlotMaterial } from './dataflow'

export interface GenerationInputSnapshot {
  referenceAssetIds: string[]
  prompt: string
  ratio: string
  quality: string
  candidateCount: number
  strength: number
  operation: 'CREATE_NEW' | 'IMAGE_TO_IMAGE' | 'CREATE_WITH_REFERENCE'
}

export type GenerationDriftState = 'CLEAN' | 'UPSTREAM_STALE' | 'CONFIG_DIRTY' | 'BOTH_STALE'

export interface ImageConfigSnapshot {
  prompt: string
  ratio: string
  quality: string
  candidateCount: number
  strength: number
  operation: 'CREATE_NEW' | 'IMAGE_TO_IMAGE' | 'CREATE_WITH_REFERENCE'
}

export interface VideoConfigSnapshot {
  prompt: string
  ratio: string
  qualityTier: string
  durationSeconds: number
  providerChannel: string | null
  engine: string
  middleTimePosition: number
  /** ADVANCED_VIDEO_GENERATION_CARD_KEYFRAME_TIMELINE_P0_V1 — pre-
   * serialized `keyframeTimeline`/`dialogueSegments` text fields
   * (Description/Motion/Camera/dialogue text) so editing a stage's own
   * context (never reflected in the image-only
   * `computeVideoUpstreamFingerprint`) still flips "需要重新生成"
   * (spec §46 persists-on-edit, not on every keystroke's OWN
   * request — this only affects the STALE badge, never triggers a
   * network call by itself). Optional — every pre-existing caller
   * (none outside `GenerateVideoNode`) is unaffected. */
  keyframeContext?: string
}

export interface SemanticUpstreamRef {
  nodeId: string
  entityId: string | null
  generatedOutputId: string | null
  slot?: 'start' | 'middle' | 'end' | 'reference' | 'audio_reference'
}

function stableRefKey(ref: SemanticUpstreamRef): string {
  return `${ref.slot ?? 'ref'}:${ref.nodeId}:${ref.entityId ?? ''}:${ref.generatedOutputId ?? ''}`
}

export function semanticRefFromImageMaterial(
  material: Pick<ImageInputMaterial, 'nodeId' | 'entityId' | 'generatedOutputId'>,
  slot?: SemanticUpstreamRef['slot'],
): SemanticUpstreamRef {
  return {
    nodeId: material.nodeId,
    entityId: material.entityId ?? null,
    generatedOutputId: material.generatedOutputId ?? null,
    slot,
  }
}

export function semanticRefFromKeyframeSlot(
  slot: KeyframeSlotMaterial,
  role: 'start' | 'middle' | 'end',
): SemanticUpstreamRef {
  return {
    nodeId: slot.nodeId,
    entityId: slot.entityId ?? null,
    generatedOutputId: slot.generatedOutputId ?? null,
    slot: role,
  }
}

export function computeUpstreamFingerprint(refs: SemanticUpstreamRef[]): string {
  const normalized = [...refs]
    .sort((a, b) => stableRefKey(a).localeCompare(stableRefKey(b)))
    .map((ref) => ({
      nodeId: ref.nodeId,
      entityId: ref.entityId,
      generatedOutputId: ref.generatedOutputId,
      slot: ref.slot ?? null,
    }))
  return JSON.stringify(normalized)
}

export function computeImageUpstreamFingerprint(materials: ImageInputMaterial[]): string {
  return computeUpstreamFingerprint(
    materials.filter((m) => m.ready).map((m) => semanticRefFromImageMaterial(m)),
  )
}

export function computeVideoUpstreamFingerprint(
  inputMaterials: InputMaterial[],
  keyframeAssignment: KeyframeAssignment,
  genericReferenceImage?: ImageInputMaterial,
  audioReferenceSlots?: KeyframeSlotMaterial[],
): string {
  const refs: SemanticUpstreamRef[] = []
  if (keyframeAssignment.start) refs.push(semanticRefFromKeyframeSlot(keyframeAssignment.start, 'start'))
  for (const middle of keyframeAssignment.middle) refs.push(semanticRefFromKeyframeSlot(middle, 'middle'))
  if (keyframeAssignment.end) refs.push(semanticRefFromKeyframeSlot(keyframeAssignment.end, 'end'))
  if (genericReferenceImage) refs.push(semanticRefFromImageMaterial(genericReferenceImage, 'reference'))
  for (const material of inputMaterials) {
    if (material.kind === 'IMAGE' && material.ready) {
      refs.push(semanticRefFromImageMaterial(material))
    }
  }
  // VistoeraTASK-00167 — structured audio references (Audio Editor
  // sibling `audio_asset` reconnects, trim exports, etc.) must change
  // the upstream fingerprint so stale failed-task errors from a prior
  // long-audio submission are not shown after the tenant fixes their
  // binding (spec §五/§九).
  for (const slot of audioReferenceSlots ?? []) {
    if (!slot.ready) continue
    refs.push({
      nodeId: slot.nodeId,
      entityId: slot.entityId ?? null,
      generatedOutputId: slot.generatedOutputId ?? null,
      slot: 'audio_reference',
    })
  }
  return computeUpstreamFingerprint(refs)
}

export function computeImageConfigFingerprint(snapshot: ImageConfigSnapshot): string {
  return JSON.stringify({
    prompt: snapshot.prompt.trim(),
    ratio: snapshot.ratio,
    quality: snapshot.quality,
    candidateCount: snapshot.candidateCount,
    strength: snapshot.strength,
    operation: snapshot.operation,
  })
}

export function computeVideoConfigFingerprint(snapshot: VideoConfigSnapshot): string {
  return JSON.stringify({
    prompt: snapshot.prompt.trim(),
    ratio: snapshot.ratio,
    qualityTier: snapshot.qualityTier,
    durationSeconds: snapshot.durationSeconds,
    providerChannel: snapshot.providerChannel,
    engine: snapshot.engine,
    middleTimePosition: snapshot.middleTimePosition,
    keyframeContext: snapshot.keyframeContext,
  })
}

/** Legacy combined fingerprint — still used for submit race guards. */
export function computeGenerationInputFingerprint(snapshot: GenerationInputSnapshot): string {
  const normalized = {
    operation: snapshot.operation,
    referenceAssetIds: [...snapshot.referenceAssetIds].sort(),
    prompt: snapshot.prompt.trim(),
    ratio: snapshot.ratio,
    quality: snapshot.quality,
    candidateCount: snapshot.candidateCount,
    strength: snapshot.strength,
  }
  return JSON.stringify(normalized)
}

/** Legacy ref ids for backward-compatible combined fingerprint (never edgeId). */
export function legacyImageReferenceAssetIds(materials: ImageInputMaterial[]): string[] {
  return materials
    .filter((m) => m.ready)
    .map((m) => m.entityId || m.generatedOutputId)
    .filter((id): id is string => !!id)
    .sort()
}

export function computeGenerationDriftState(args: {
  hasResult: boolean
  needsRegenerationFlag?: boolean
  generatedUpstreamFingerprint?: string
  generatedConfigFingerprint?: string
  generatedFromFingerprint?: string
  currentUpstreamFingerprint: string
  currentConfigFingerprint: string
  legacyReferenceAssetIds?: string[]
  legacyConfigSnapshot?: ImageConfigSnapshot | VideoConfigSnapshot
}): GenerationDriftState {
  if (!args.hasResult) return 'CLEAN'
  if (args.needsRegenerationFlag) return 'UPSTREAM_STALE'

  if (args.generatedUpstreamFingerprint && args.generatedConfigFingerprint) {
    const upstreamStale = args.generatedUpstreamFingerprint !== args.currentUpstreamFingerprint
    const configDirty = args.generatedConfigFingerprint !== args.currentConfigFingerprint
    if (upstreamStale && configDirty) return 'BOTH_STALE'
    if (upstreamStale) return 'UPSTREAM_STALE'
    if (configDirty) return 'CONFIG_DIRTY'
    return 'CLEAN'
  }

  if (args.generatedFromFingerprint && args.legacyReferenceAssetIds && args.legacyConfigSnapshot) {
    const imageConfig = args.legacyConfigSnapshot as ImageConfigSnapshot
    const currentCombined = computeGenerationInputFingerprint({
      referenceAssetIds: args.legacyReferenceAssetIds,
      prompt: imageConfig.prompt,
      ratio: imageConfig.ratio,
      quality: imageConfig.quality,
      candidateCount: imageConfig.candidateCount,
      strength: imageConfig.strength,
      operation: imageConfig.operation,
    })
    if (args.generatedFromFingerprint === currentCombined) return 'CLEAN'

    try {
      const parsed = JSON.parse(args.generatedFromFingerprint) as { referenceAssetIds?: string[] }
      const legacyRefIds = [...(parsed.referenceAssetIds || [])].sort()
      const currentRefIds = [...args.legacyReferenceAssetIds].sort()
      const upstreamStale = JSON.stringify(legacyRefIds) !== JSON.stringify(currentRefIds)
      const configDirty = computeImageConfigFingerprint(imageConfig) !== args.currentConfigFingerprint

      if (upstreamStale && configDirty) return 'BOTH_STALE'
      if (upstreamStale) return 'UPSTREAM_STALE'
      if (configDirty) return 'CONFIG_DIRTY'
      return 'CLEAN'
    } catch {
      return 'CLEAN'
    }
  }

  return 'CLEAN'
}

export function computeVideoGenerationDriftState(args: {
  hasResult: boolean
  needsRegenerationFlag?: boolean
  generatedUpstreamFingerprint?: string
  generatedConfigFingerprint?: string
  generatedFromFingerprint?: string
  currentUpstreamFingerprint: string
  currentConfigFingerprint: string
}): GenerationDriftState {
  if (!args.hasResult) return 'CLEAN'
  if (args.needsRegenerationFlag) return 'UPSTREAM_STALE'

  if (args.generatedUpstreamFingerprint && args.generatedConfigFingerprint) {
    const upstreamStale = args.generatedUpstreamFingerprint !== args.currentUpstreamFingerprint
    const configDirty = args.generatedConfigFingerprint !== args.currentConfigFingerprint
    if (upstreamStale && configDirty) return 'BOTH_STALE'
    if (upstreamStale) return 'UPSTREAM_STALE'
    if (configDirty) return 'CONFIG_DIRTY'
    return 'CLEAN'
  }

  if (args.generatedFromFingerprint) {
    try {
      const parsed = JSON.parse(args.generatedFromFingerprint) as VideoConfigSnapshot & {
        referenceEdgeIds?: string[]
        qualityTier?: string
        prompt?: string
      }
      const legacyConfig = computeVideoConfigFingerprint({
        prompt: parsed.prompt ?? '',
        ratio: parsed.ratio ?? '16:9',
        qualityTier: parsed.qualityTier ?? '1080P',
        durationSeconds: parsed.durationSeconds ?? 5,
        providerChannel: parsed.providerChannel ?? null,
        engine: parsed.engine ?? 'auto',
        middleTimePosition: parsed.middleTimePosition ?? 3,
      })
      if (legacyConfig !== args.currentConfigFingerprint) return 'CONFIG_DIRTY'
      return 'CLEAN'
    } catch {
      return 'CLEAN'
    }
  }

  return 'CLEAN'
}

export function generationDriftRequiresRegeneration(state: GenerationDriftState): boolean {
  return state !== 'CLEAN'
}

export function generationDriftBannerText(state: GenerationDriftState, t: TFunction<'canvas'>): string | null {
  switch (state) {
    case 'CLEAN':
      return null
    case 'UPSTREAM_STALE':
      return t('generation.driftBannerUpstreamStale')
    case 'CONFIG_DIRTY':
      return t('generation.driftBannerConfigDirty')
    case 'BOTH_STALE':
      return t('generation.driftBannerBothStale')
  }
}

export function generationDriftInspectorText(state: GenerationDriftState, t: TFunction<'canvas'>): string | null {
  switch (state) {
    case 'CLEAN':
      return null
    case 'UPSTREAM_STALE':
      return t('generation.driftInspectorUpstreamStale')
    case 'CONFIG_DIRTY':
      return t('generation.driftInspectorConfigDirty')
    case 'BOTH_STALE':
      return t('generation.driftInspectorBothStale')
  }
}

/** @deprecated Use computeGenerationDriftState — kept for callers still on combined compare. */
export function isGenerationStale(
  generatedFromFingerprint: string | undefined,
  currentFingerprint: string,
  hasResult: boolean,
): boolean {
  if (!hasResult) return false
  if (!generatedFromFingerprint) return false
  return generatedFromFingerprint !== currentFingerprint
}

export const TRANSIENT_FIELD_PENDING_AUTO_GENERATE = 'pendingAutoGenerate'

export function stripTransientGenerationFields(fields: Record<string, unknown>): Record<string, unknown> {
  const next = { ...fields }
  delete next[TRANSIENT_FIELD_PENDING_AUTO_GENERATE]
  return next
}

export function normalizePersistedGenerationFields(fields: Record<string, unknown>): Record<string, unknown> {
  return stripTransientGenerationFields(fields)
}
